import { useUpdated } from "@common/lib/hooks";
import * as React from "react";

export interface WithValueProps<T> {
  onBlur?: () => void;
  onChange: (value: T) => any;
  onChangeTrigger?: number | "blur";
  onFocus?: () => void;
}

export type PublicProps<T, U> = U & WithValueProps<T>;
export type Props<T, U> = PublicProps<T, U>;

export interface State<T> {
  isFocused: boolean;
  value?: T;
}

export default <T, OriginalProps extends {}>(Cmp: React.ComponentType<any>, propName: string = "value") =>
  (props: Props<T, OriginalProps> & { ref?: React.RefObject<any> }) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [value, setValue] = React.useState(props.onChangeTrigger ? props[propName] : undefined);

    const onChangeTimeout = React.useRef<any>(null);

    useUpdated(() => {
      if (props.onChangeTrigger) {
        setValue(props[propName]);

        onChangeTimeout.current && clearTimeout(onChangeTimeout.current);
      }
    }, [props[propName], props.onChangeTrigger]);

    React.useEffect(
      () => () => {
        onChangeTimeout.current && clearTimeout(onChangeTimeout.current);
      },
      []
    );

    const onBlur = React.useCallback(() => {
      if (props.onChangeTrigger === "blur") props.onChange(value);

      setIsFocused(false);

      props.onBlur && props.onBlur();
    }, [props.onBlur, props.onChange, props.onChangeTrigger, value]);

    const onFocus = React.useCallback(() => {
      setIsFocused(true);

      props.onFocus && props.onFocus();
    }, [props.onFocus]);

    const onChange = React.useCallback(
      (value: T) => {
        if (props.onChangeTrigger) {
          setValue(value);

          if (props.onChangeTrigger === "blur") {
            if (!isFocused) props.onChange(value);
          } else {
            onChangeTimeout.current && clearTimeout(onChangeTimeout.current);

            onChangeTimeout.current = setTimeout(() => props.onChange(value), props.onChangeTrigger);
          }
        } else {
          props.onChange(value);
        }
      },
      [props.onChangeTrigger, isFocused, props.onChange]
    );

    const addProps = {
      [propName]: value ?? props[propName]
    };
    // IMPORTANT to not pass in onChangeTrigger
    const { onChangeTrigger, ...ownProps } = props;

    return (
      <Cmp
        {...ownProps}
        {...addProps}
        onBlur={onChangeTrigger === "blur" ? onBlur : props.onBlur}
        onFocus={onChangeTrigger === "blur" ? onFocus : props.onFocus}
        onChange={onChange}
      />
    );
  };
